import { IStackStyles, ITheme } from '@fluentui/react';
import { globalBoxBorderRadius } from 'globalStyles';
import { AnnouncementStatus, AnnouncementType } from 'models/announcement';

//a link to one announcement: ?announcement=<id> on any page of the app opens the panel at it
const announcementQueryParam = 'announcement';

//a link to the dashboard; it carries no organization, as every organization reads the same announcements
export const getAnnouncementLink = (announcementId: number): string =>
  `${window.location.origin}/dashboard?${announcementQueryParam}=${announcementId}`;

//the announcement a link in `search` (the query of the address) points to, or undefined
export const readAnnouncementLink = (search: string): number | undefined => {
  const announcementId = Number(new URLSearchParams(search).get(announcementQueryParam));

  return Number.isInteger(announcementId) && announcementId > 0 ? announcementId : undefined;
};

//`search` without the link to an announcement
export const removeAnnouncementLink = (search: string): string => {
  const searchParams = new URLSearchParams(search);
  searchParams.delete(announcementQueryParam);
  const rest = searchParams.toString();

  return rest ? `?${rest}` : '';
};

//the width of the panel of the reader, shared by the panel and by the preview in Tenant Admin that draws it
export const announcementsPanelWidth = 440;

//the frame of an announcement card, shared by the card and its placeholder
export const getAnnouncementCardFrameStyles = (theme: ITheme): IStackStyles => ({
  root: {
    border: `1px solid ${theme.semanticColors.cardBorder}`,
    borderRadius: globalBoxBorderRadius,
  },
});

//the tag colour of an announcement type, from the `announcementType*` tokens of the theme
export const getAnnouncementTypeColor = (announcementType: AnnouncementType, theme: ITheme): string => {
  switch (announcementType) {
    case AnnouncementType.ReleaseNotes:
      return theme.semanticColors.announcementTypeReleaseNotes;
    case AnnouncementType.NewFeature:
      return theme.semanticColors.announcementTypeNewFeature;
    default:
      return theme.semanticColors.announcementTypeNews;
  }
};

//the tag colour of an announcement status, from the `announcementStatus*` tokens of the theme
export const getAnnouncementStatusColor = (status: AnnouncementStatus, theme: ITheme): string => {
  switch (status) {
    case AnnouncementStatus.Scheduled:
      return theme.semanticColors.announcementStatusScheduled;
    case AnnouncementStatus.Live:
      return theme.semanticColors.announcementStatusLive;
    case AnnouncementStatus.Expired:
      return theme.semanticColors.announcementStatusExpired;
    case AnnouncementStatus.Withdrawn:
      return theme.semanticColors.announcementStatusWithdrawn;
    default:
      return theme.semanticColors.announcementStatusDraft;
  }
};

//
// Each filter group has a defined key that ends with a $ sign. A filter value is the string {group}${value}.
// The quick filters store their values as {field}#{count1}@{field}#{count2}@{...} and the scene filter
// stores them comma separated, so a value cannot contain $, @, # or a comma.
// Type and status values are the numbers of AnnouncementType and AnnouncementStatus.
//
export enum FilterAnnouncementGroupKeys {
  type = 'type$',
  status = 'status$',
  review = 'review$',
}

//The only value of the review group: the announcement holds an AI translation that is not reviewed yet.
export const FilterAnnouncementNeedsReviewValue = 'unreviewed';

/**
 * The window event that tells the megaphone and an open reader panel that what readers see may have
 * changed. The write calls of `announcementService` send it after a publish, a withdraw, a save of a
 * published item and a delete; the dot and the panel read again.
 */
const announcementsChangedEvent = 'isoplanner:announcements-changed';

export const notifyAnnouncementsChanged = (): void => {
  window.dispatchEvent(new Event(announcementsChangedEvent));
};

//returns the function that stops listening
export const onAnnouncementsChanged = (listener: () => void): (() => void) => {
  window.addEventListener(announcementsChangedEvent, listener);

  return () => window.removeEventListener(announcementsChangedEvent, listener);
};
